/**
 * i18n - Internationalization module for InterPals
 *
 * Provides translation functionality for JavaScript code.
 * Translations are loaded from PO files converted to JSON at build time.
 *
 * Usage:
 *   import { t } from './i18n.js';
 *   const message = t('common.save');
 *   const messageWithParams = t('pm.message_count', { count: 5 });
 *
 * The module is also exposed globally as window.I18n and window.t
 */

/**
 * @typedef {Object} I18nConfig
 * @property {string} locale - Current locale (e.g., 'en_US', 'fr_FR')
 * @property {boolean} isRtl - Whether the current locale is RTL
 * @property {Object<string, string>} translations - Translation key-value pairs
 */

/**
 * Parse ICU MessageFormat select options using brace-depth counting.
 * Handles nested braces correctly (e.g., "male{He {name}}" extracts "He {name}").
 *
 * @param {string} optionsStr - Options string like "male{text1} female{text2} other{default}"
 * @returns {Object<string, string>} Parsed options map
 */
function parseSelectOptions(optionsStr) {
    const options = {};
    let i = 0;

    while (i < optionsStr.length) {
        // Skip whitespace
        while (i < optionsStr.length && /\s/.test(optionsStr[i])) i++;
        if (i >= optionsStr.length) break;

        // Read option name (word characters until '{')
        let nameStart = i;
        while (i < optionsStr.length && optionsStr[i] !== '{' && !/\s/.test(optionsStr[i])) i++;
        const name = optionsStr.substring(nameStart, i).trim();
        if (!name) break;

        // Skip whitespace before '{'
        while (i < optionsStr.length && /\s/.test(optionsStr[i])) i++;
        if (i >= optionsStr.length || optionsStr[i] !== '{') break;

        // Find matching '}' using brace-depth counting
        i++; // skip opening '{'
        let depth = 1;
        let valueStart = i;
        while (i < optionsStr.length && depth > 0) {
            if (optionsStr[i] === '{') depth++;
            else if (optionsStr[i] === '}') depth--;
            i++;
        }

        if (depth !== 0) break; // Malformed - unbalanced braces

        // Extract value (everything between '{' and matching '}')
        options[name] = optionsStr.substring(valueStart, i - 1);
    }

    return options;
}

/**
 * Parse and evaluate ICU MessageFormat select patterns
 * Supports: {variable, select, value1{text1} value2{text2} other{default}}
 * Handles nested braces: {gender, select, male{He {name}} female{She {name}} other{They}}
 *
 * @param {string} text - Text containing select pattern
 * @param {Object<string, string|number>} params - Parameters including the select variable
 * @returns {string} Text with select pattern resolved
 */
function resolveSelectPatterns(text, params) {
    // Find {variable, select, and then parse until matching closing brace
    let result = text;
    let startIdx;

    while ((startIdx = result.indexOf(', select,')) !== -1) {
        // Find the opening brace before ", select,"
        let braceStart = startIdx - 1;
        while (braceStart >= 0 && result[braceStart] !== '{') {
            braceStart--;
        }
        if (braceStart < 0) break;

        // Extract variable name
        const variable = result.substring(braceStart + 1, startIdx).trim();
        const value = params[variable];

        // Find matching closing brace by counting brace depth
        let depth = 1;
        let pos = startIdx + 9; // length of ", select,"
        while (pos < result.length && depth > 0) {
            if (result[pos] === '{') depth++;
            else if (result[pos] === '}') depth--;
            pos++;
        }

        if (depth !== 0) break;

        // Extract options string (between ", select," and final "}")
        const optionsStr = result.substring(startIdx + 9, pos - 1).trim();

        // Parse options using brace-depth counting (handles nested braces)
        const options = parseSelectOptions(optionsStr);

        // Get replacement value
        const replacement = (value !== undefined && options[value])
            ? options[value]
            : (options['other'] ?? result.substring(braceStart, pos));

        // Replace the entire select pattern
        result = result.substring(0, braceStart) + replacement + result.substring(pos);
    }

    return result;
}

/** @type {string} localStorage key for cached translations */
const CACHE_KEY = 'i18n_cache';

/**
 * I18n singleton for managing translations
 */
const I18n = {
    /** @type {string} Current locale */
    locale: 'en_US',

    /** @type {boolean} Whether current locale uses RTL */
    isRtl: false,

    /** @type {Object<string, string>} Translation dictionary */
    translations: {},

    /** @type {boolean} Whether the module has been initialized */
    _initialized: false,

    /** @type {Promise|null} Promise that resolves when async loading completes */
    _loadingPromise: null,

    /** @type {string|null} Current translations version for cache validation */
    _version: null,

    /**
     * Initialize the i18n module with configuration
     * @param {I18nConfig} config - Configuration object
     */
    init(config) {
        if (!config) {
            console.warn('[i18n] init called without config');
            return;
        }

        this.locale = config.locale || 'en_US';
        this.isRtl = config.isRtl || false;
        this.translations = config.translations || {};
        this._initialized = true;

        // Note: Document dir and lang attributes are set server-side in base.twig
        // We only store the values here for reference by JS code

        // Debug info in development
        if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') {
            console.debug(`[i18n] Initialized with locale: ${this.locale}, RTL: ${this.isRtl}, keys: ${Object.keys(this.translations).length}`);
        }
    },

    /**
     * Translate a key with optional parameter substitution
     * @param {string} key - Translation key (e.g., 'common.save', 'pm.message_count')
     * @param {Object<string, string|number>} [params] - Parameters to substitute
     * @returns {string} Translated string or the key if not found
     *
     * @example
     * t('common.save') // Returns "Save" or "Enregistrer" etc.
     * t('pm.message_count', { count: 5 }) // Returns "5 messages"
     * t('common.activity_liked', { gender: 'female' }) // Returns gender-specific text
     */
    t(key, params = {}) {
        if (!key) {
            return '';
        }

        // Get translation or fall back to key
        let text = this.translations[key];

        if (text === undefined) {
            // In development, warn about missing translations
            if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') {
                console.warn(`[i18n] Missing translation: "${key}"`);
            }
            text = key;
        }

        if (params && typeof params === 'object') {
            // First, resolve ICU select patterns: {gender, select, male{он} female{она} other{они}}
            if (text.includes(', select,')) {
                text = resolveSelectPatterns(text, params);
            }

            // Then substitute simple parameters: {name} -> value, %name% -> value, :name -> value
            Object.keys(params).forEach(param => {
                const value = String(params[param]);
                // Handle {param} syntax (ICU style)
                text = text.replaceAll(`{${param}}`, value);
                // Handle %param% syntax (Symfony style)
                text = text.replaceAll(`%${param}%`, value);
                // Handle :param syntax (Laravel style). Match only when not followed by a word character.
                text = text.replace(new RegExp(`:${param}(?![a-zA-Z0-9_])`, 'g'), value);
            });
        }

        return text;
    },

    /**
     * Check if a translation key exists
     * @param {string} key - Translation key to check
     * @returns {boolean} True if the key exists
     */
    has(key) {
        return key in this.translations;
    },

    /**
     * Get all translation keys (useful for debugging)
     * @returns {string[]} Array of translation keys
     */
    keys() {
        return Object.keys(this.translations);
    },

    /**
     * Get current locale
     * @returns {string} Current locale code
     */
    getLocale() {
        return this.locale;
    },

    /**
     * Check if current locale is RTL
     * @returns {boolean} True if RTL
     */
    isRtlLocale() {
        return this.isRtl;
    },

    /**
     * Dynamically add translations (useful for lazy loading)
     * @param {Object<string, string>} newTranslations - Translations to add
     */
    addTranslations(newTranslations) {
        if (newTranslations && typeof newTranslations === 'object') {
            Object.assign(this.translations, newTranslations);
        }
    },

    /**
     * Load translations from a URL with localStorage caching
     * @param {string} url - URL to fetch translations from
     * @param {string} version - Version hash for cache validation
     * @returns {Promise<void>} Resolves when translations are loaded
     */
    async loadFromUrl(url, version) {
        this._version = version;

        // Check localStorage cache first
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) {
                const data = JSON.parse(cached);
                if (data.version === version && data.locale === this.locale) {
                    this.addTranslations(data.translations);
                    this._dispatchReadyEvent();
                    if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') {
                        console.debug(`[i18n] Loaded ${Object.keys(data.translations).length} keys from cache (v${version})`);
                    }
                    return;
                }
            }
        } catch (e) {
            // Cache corrupted or localStorage unavailable - clear and proceed with fetch
            if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') {
                console.debug('[i18n] Cache read failed, fetching fresh:', e.message);
            }
            this.clearCache();
        }

        // Fetch from server
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const translations = await response.json();
            this.addTranslations(translations);

            // Save to localStorage cache
            try {
                localStorage.setItem(CACHE_KEY, JSON.stringify({
                    version,
                    locale: this.locale,
                    translations
                }));
            } catch (e) {
                // localStorage quota exceeded or unavailable - try clearing old cache and retry once
                this.clearCache();
                try {
                    localStorage.setItem(CACHE_KEY, JSON.stringify({
                        version,
                        locale: this.locale,
                        translations
                    }));
                } catch {
                    // Still failed after clearing - continue without caching
                    if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') {
                        console.debug('[i18n] localStorage unavailable:', e.message);
                    }
                }
            }

            this._dispatchReadyEvent();

            if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') {
                console.debug(`[i18n] Fetched ${Object.keys(translations).length} keys from ${url}`);
            }
        } catch (error) {
            console.error(`[i18n] Failed to load translations from ${url}:`, error);
        }
    },

    /**
     * Signal that translations are ready
     * Updates Alpine store for reactive re-renders and dispatches event for other listeners
     * @private
     */
    _dispatchReadyEvent() {
        if (typeof window !== 'undefined') {
            // Update Alpine store if available (preferred method for Alpine components)
            // This handles both "already initialized" and "not yet initialized" cases
            if (window.Alpine?.store('i18n')) {
                window.Alpine.store('i18n').ready = true;
            }

            // Also dispatch event for non-Alpine listeners
            window.dispatchEvent(new CustomEvent('i18n:ready', {
                detail: { locale: this.locale, keyCount: Object.keys(this.translations).length }
            }));
        }
    },

    /**
     * Wait for any in-progress translation loading to complete
     * @returns {Promise<void>} Resolves when translations are ready
     */
    ready() {
        return this._loadingPromise || Promise.resolve();
    },

    /**
     * Clear the localStorage translation cache
     */
    clearCache() {
        try {
            localStorage.removeItem(CACHE_KEY);
        } catch {
            // Ignore errors
        }
    }
};

/**
 * Shorthand translation function
 * @param {string} key - Translation key
 * @param {Object<string, string|number>} [params] - Parameters to substitute
 * @returns {string} Translated string
 */
function t(key, params) {
    return I18n.t(key, params);
}

// Expose globally for use in inline scripts and Alpine.js
if (typeof window !== 'undefined') {
    window.I18n = I18n;
    window.t = t;
    window.trans = (domain, key, params) => I18n.t(`${domain}.${key}`, params);

    // Auto-initialize from __i18nConfig if available (set before bundle loads)
    // This ensures translations are available immediately when Alpine renders
    if (window.__i18nConfig && !I18n._initialized) {
        I18n.init(window.__i18nConfig);

        // If translationsUrl is provided, load translations async from static JSON
        // This replaces the inline translations with cached/fetched ones
        if (window.__i18nConfig.translationsUrl && window.__i18nConfig.version) {
            I18n._loadingPromise = I18n.loadFromUrl(
                window.__i18nConfig.translationsUrl,
                window.__i18nConfig.version
            );
        }
    }
}

export { I18n, t };
export default I18n;
